import { List, Map } from "immutable";
import { Moment } from "moment";
import { createSelector } from "reselect";

import {
  composeSelector,
  composeSelectors
} from "seneca-common/utils/selectors/compose-selectors";

import { ClassId } from "../../../../../class/state";
import { PATH_TO_TEACHER_ASSIGNMENTS_STATE } from "../consts";
import TeacherAssignment from "../models/TeacherAssignment";
import TeacherAssignmentsState from "../models/TeacherAssignmentsState";
import { getAssignments } from "./state";
import * as teacherAssignments from "./state/assignments/teacherAssignments";

type State = any;
type AssignmentId = string;
type CourseId = string;
type SectionId = string;
type QuestionId = string;
type UserId = string;

export function getTeacherAssignmentsState(
  state: State
): TeacherAssignmentsState {
  return state.get(PATH_TO_TEACHER_ASSIGNMENTS_STATE);
}

export const getStateAssignments: (
  state: State
) => Map<AssignmentId, TeacherAssignment> = composeSelector(
  getTeacherAssignmentsState,
  getAssignments
);

// @ts-ignore
export const getAssignment: (
  state: any,
  assignmentId: AssignmentId
) => TeacherAssignment = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignment
);

export const getAssignmentSectionLastSetDate: (
  state: any,
  sectionId: SectionId
) => string | undefined = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentSectionLastSetDate
);

export const getAssignmentStartDate: (
  state: any,
  assignmentId: AssignmentId
) => string = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentStartDate
);

export const getAssignmentDueDate: (
  state: any,
  assignmentId: AssignmentId
) => string = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentDueDate
);

export const getAssignmentName: (
  state: any,
  assignmentId: AssignmentId
) => string = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentName
);

export const getAssignmentNumberOfAssignees: (
  state: any,
  assignmentId: AssignmentId
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentNumOfAssignees
);

export const getAssignmentCourseId: (
  state: any,
  assignmentId: AssignmentId
) => CourseId = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentCourseId
);

export const getAssignmentSectionIds: (
  state: any,
  assignmentId: AssignmentId
) => List<SectionId> = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentSectionIds
);

export const getAssignmentExamQuestionIds: (
  state: any,
  assignmentId: AssignmentId
) => List<QuestionId> = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentExamQuestionIds
);

export const getAssignmentNumberOfExamQuestions: (
  state: any,
  assignmentId: AssignmentId
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentNumberOfExamQuestions
);

export const getAssignmentAssigneesWithSections: (
  state: any,
  assignmentId: AssignmentId
) => Map<UserId, List<SectionId>> = composeSelector(
  getStateAssignments,
  teacherAssignments.getAssigneesWithSections
);

export const getAssignmentNumberOfSections: (
  state: any,
  assignmentId: AssignmentId
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentNumberOfSections
);

export const getAssignmentAssignees: (
  state: any,
  assignmentId: AssignmentId
) => List<UserId> = composeSelector(
  getStateAssignments,
  teacherAssignments.getAssignmentAssignees
);

export const getIsPastAssignment = (
  state: any,
  assignmentId: AssignmentId,
  now: Moment
): boolean => {
  const assignmentsState = getStateAssignments(state);
  return teacherAssignments.getIsPastAssignment(
    assignmentsState,
    assignmentId,
    now
  );
};

export const getIsAssignmentAfterCutoffDate = (
  state: State,
  assignmentId: AssignmentId,
  now: Moment
): boolean => {
  const assignmentsState = getStateAssignments(state);
  return teacherAssignments.getIsAssignmentAfterCutoffDate(
    assignmentsState,
    assignmentId,
    now
  );
};

export const getHasNoAssignees: (
  state: any,
  assignmentId: AssignmentId
) => boolean = createSelector(
  getAssignmentNumberOfAssignees,
  number => number === 0
);

export const makeGetClassAssignmentIds = (classId: AssignmentId) => {
  return createSelector(getStateAssignments, assignments =>
    teacherAssignments.getClassAssignmentIds(assignments as any, classId)
  );
};

export const makeGetNumberOfClassAssignments = (
  classId: AssignmentId
): ((state: State) => number) => {
  return createSelector(getStateAssignments, assignments =>
    teacherAssignments.getNumberOfClassAssignments(assignments as any, classId)
  );
};

export const assignmentExists: (
  state: any,
  assignmentId: AssignmentId
) => boolean = composeSelector(
  getStateAssignments,
  teacherAssignments.assignmentExists
);

export const getAssignmentSpecType: (
  state: any,
  assignmentId: AssignmentId
) => string = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentSpecType
);

export const getAssignmentIsSmart: (
  state: any,
  assignmentId: AssignmentId
) => boolean = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentIsSmart
);

export const getAutoEnrolNewStudents: (
  state: any,
  assignmentId: AssignmentId
) => boolean = composeSelector(
  getStateAssignments,
  teacherAssignments.getAutoEnrolNewStudents
);

export const getAssignmentCutoffDate: (
  state: any,
  assignmentId: AssignmentId
) => string = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getAssignmentCutoffDate
);

export const makeGetNumberOfUpcomingClassAssignments = (
  classId: ClassId,
  now: Moment
): ((state: State) => number) =>
  createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfUpcomingClassAssignments(
      assignmentsState as any,
      classId,
      now
    )
  );

export const assignmentIncludesExamQuestions: (
  state: any,
  assignmentId: string
) => boolean = composeSelector(
  getStateAssignments as any,
  teacherAssignments.assignmentIncludesExamQuestions
);

export const assignmentHasNoSections: (
  state: any,
  assignmentId: string
) => boolean = composeSelector(
  getStateAssignments as any,
  teacherAssignments.assignmentHasNoSections
);

export const makeGetNumberOfUpcomingClassAssignmentsWithExamQuestions = (
  classId: string,
  now: Moment
) => {
  return createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfUpcomingClassAssignmentsWithExamQuestions(
      assignmentsState as any,
      classId,
      now
    )
  );
};

export const makeGetNextDueAssignmentWithExamQuestions = (
  classId: string,
  now: Moment
) => {
  return createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNextDueAssignmentWithExamQuestions(
      assignmentsState as any,
      classId,
      now
    )
  );
};

export const makeGetNumberOfPastClassAssignments = (
  assignmentId: string,
  now: Moment
): ((state: State) => number) =>
  createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfPastClassAssignments(
      assignmentsState as any,
      assignmentId,
      now
    )
  );

export const makeGetNumberOfAnswersNeedMarking = (
  assignmentId: string,
  questionIds?: string[]
): ((state: State) => number) =>
  createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfAnswersNeedMarking(
      assignmentsState as any,
      assignmentId,
      questionIds
    )
  );

export const makeGetNumberOfAnswersNotRemarked = (
  assignmentId: string,
  questionIds?: string[]
): ((state: State) => number) => {
  return createSelector(getStateAssignments, assignmentsState =>
    teacherAssignments.getNumberOfAnswersNotRemarked(
      assignmentsState as any,
      assignmentId,
      questionIds
    )
  );
};

export const getNumberOfStudentsNeedMarking: (
  state: any,
  assignmentId: string
) => number = composeSelector(
  getStateAssignments as any,
  teacherAssignments.getNumberOfStudentsNeedMarking
);

export const {
  getAssignmentSectionAverageScore,
  getAssigneeStatus,
  getAssigneeAggregatedStats,
  getAssigneeTotalStudyTime,
  getAssigneeAverageScore,
  getAssigneeSectionStats,
  getAssigneeIndividualSectionStats,
  getNumberOfAssigneesWithStatus,
  getAssignees,
  getAssigneeSectionIds,
  isAssignmentWithStats,
  getIsAfterAssignmentDueDate,
  getSmartAssignmentSectionsFromConditions,
  getAssignee,
  getAssigneeQuestionStats,
  getAssigneeIndividualQuestionStats,
  getAssignmentQuestionsAverageMark,
  getAssignmentQuestionsMarksTotal,
  getAssignmentQuestionAverageMark,
  getAssignmentQuestionMarksTotal,
  getAssigneeQuestionTeacherMark,
  getAssigneeQuestionEmbeddedAnswerMarks,
  getAssigneeQuestionAutoMark,
  getAssigneeQuestionConfidence,
  getAssigneeQuestionTotalMarks,
  getAssigneeQuestionSelfMark,
  getAssigneeQuestionFlagged,
  getAssigneeQuestionNeedsMarking,
  getNumberOfAssigneesInProgress
} = composeSelectors(getStateAssignments as any, teacherAssignments as any);
